import { DataQualityClassification } from '@repo/common/enums/dataQualityClassification';
import { getScoreClassification } from '@repo/common/utils/dataQuality';
import type { ColorNames } from '@repo/theme/utils';
import { memo } from 'react';
import { BaseProgressIndicator } from '../../ProgressIndicator/BaseProgressIndicator';
import { DataQualityBadge } from '../DataQualityBadge';
import { QualityScorePercentage } from './QualityScorePercentage';
import { QualityScoreRingBody } from './QualityScoreRingBody';
import { SIZE_CONFIG, type SizeOptions } from './constants';

const CHART_COLORS: Record<
	DataQualityClassification,
	{ lineColor: ColorNames; hoverLineColor: ColorNames }
> = {
	[DataQualityClassification.GREAT]: {
		lineColor: 'fill/success/default',
		hoverLineColor: 'fill/success-secondary/default',
	},
	[DataQualityClassification.GOOD]: {
		lineColor: 'fill/info/default',
		hoverLineColor: 'fill/info-secondary/default',
	},
	[DataQualityClassification.MODERATE]: {
		lineColor: 'fill/caution/default',
		hoverLineColor: 'fill/caution-secondary/default',
	},
	[DataQualityClassification.POOR]: {
		lineColor: 'fill/warning/default',
		hoverLineColor: 'fill/warning-secondary/default',
	},
	[DataQualityClassification.UNAVAILABLE]: {
		lineColor: 'fill/secondary/default',
		hoverLineColor: 'fill/secondary/hover',
	},
} as const;

const BORDER_WIDTH_CONFIG = {
	sm: 2,
	md: 8,
} as const;

export interface QualityScoreRingProps {
	score: number;
	size?: SizeOptions;
	displayBadge?: boolean;
	showInnerContent?: boolean;
	borderWidth?: keyof typeof BORDER_WIDTH_CONFIG;
}

export const QualityScoreRing = memo(
	({
		score,
		size = 'md',
		displayBadge = true,
		showInnerContent = true,
		borderWidth = 'md',
	}: QualityScoreRingProps) => {
		const classification = getScoreClassification(score);

		return (
			<QualityScoreRingBody
				progressIndicator={
					<BaseProgressIndicator
						size={SIZE_CONFIG[size]}
						value={score}
						backgroundLineColor="fill/tertiary/default"
						animate
						borderWidth={BORDER_WIDTH_CONFIG[borderWidth]}
						lineColor={CHART_COLORS[classification].lineColor}
					/>
				}
			>
				{showInnerContent &&
					classification !== DataQualityClassification.UNAVAILABLE && (
						<QualityScorePercentage score={score} size={size} />
					)}
				{showInnerContent && displayBadge && (
					<DataQualityBadge classification={classification} />
				)}
			</QualityScoreRingBody>
		);
	}
);
QualityScoreRing.displayName = 'QualityScoreRing';
